import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { type RootState } from '@reducers';
import {
  Collapse,
  Progress,
  type ProgressProps,
  Space,
  Tag,
  theme,
  Typography,
} from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

import { WAREHOUSE_CATEGORY_SUGGESTION_STAGE_LABELS } from '../../fragments/warehouse-category-suggestion-names';
import {
  EWarehouseCategorySuggestionItemStatus,
  EWarehouseCategorySuggestionsRunStatus,
  type IWarehouseCategorySuggestionItem,
} from '../../reducers/categories/types';
import { SPACING } from '../../theme/tokens';
import Link from '../link';

const { Text } = Typography;

const CHECKLIST_MAX_HEIGHT = '20rem';
const MINUTE_MS = 60000;

const ITEM_STATUS_LABELS: Record<
  EWarehouseCategorySuggestionItemStatus,
  string
> = {
  [EWarehouseCategorySuggestionItemStatus.PENDING]: 'Pending',
  [EWarehouseCategorySuggestionItemStatus.PROCESSING]: 'Processing',
  [EWarehouseCategorySuggestionItemStatus.SUGGESTED]: 'Suggested',
  [EWarehouseCategorySuggestionItemStatus.NO_SUGGESTION]: 'No suggestion',
  [EWarehouseCategorySuggestionItemStatus.FAILED]: 'Failed',
};

const FINISHED_ITEM_STATUSES: EWarehouseCategorySuggestionItemStatus[] = [
  EWarehouseCategorySuggestionItemStatus.SUGGESTED,
  EWarehouseCategorySuggestionItemStatus.NO_SUGGESTION,
  EWarehouseCategorySuggestionItemStatus.FAILED,
];

export const formatMinutes = (ms: number): string => {
  const minutes: number = Math.max(1, Math.round(ms / MINUTE_MS));

  return `${minutes} min`;
};

export const getRemainingMs = ({
  startedAt,
  now,
  currentBatch,
  totalBatches,
}: {
  startedAt: number | undefined;
  now: number;
  currentBatch: number;
  totalBatches: number;
}): number | null => {
  const completedBatches: number = currentBatch - 1;

  if (startedAt === undefined || completedBatches < 1) {
    return null;
  }

  const msPerBatch: number = (now - startedAt) / completedBatches;

  return msPerBatch * (totalBatches - completedBatches);
};

const getProgressStatus = (
  status: EWarehouseCategorySuggestionsRunStatus,
  failedBatchesCount: number,
): ProgressProps['status'] => {
  switch (status) {
    case EWarehouseCategorySuggestionsRunStatus.PREPARING:
    case EWarehouseCategorySuggestionsRunStatus.RUNNING:
      return 'active';
    case EWarehouseCategorySuggestionsRunStatus.FAILED:
      return 'exception';
    case EWarehouseCategorySuggestionsRunStatus.DONE:
      return failedBatchesCount > 0 ? 'normal' : 'success';
    default:
      return 'normal';
  }
};

function WarehouseCategorySuggestionsProgress(): React.ReactElement | null {
  const { token } = theme.useToken();
  const {
    stage,
    status,
    items,
    suggestions,
    batch,
    failedBatchesCount,
    cachedCount,
    startedAt,
    finishedAt,
  } = useSelector((s: RootState) => s.Categories.warehouseCategorySuggestions);

  if (status === EWarehouseCategorySuggestionsRunStatus.IDLE) {
    return null;
  }

  const itemIcons: Record<
    EWarehouseCategorySuggestionItemStatus,
    React.ReactElement
  > = {
    [EWarehouseCategorySuggestionItemStatus.PENDING]: (
      <ClockCircleOutlined style={{ color: token.colorTextTertiary }} />
    ),
    [EWarehouseCategorySuggestionItemStatus.PROCESSING]: (
      <LoadingOutlined style={{ color: token.colorPrimary }} />
    ),
    [EWarehouseCategorySuggestionItemStatus.SUGGESTED]: (
      <CheckCircleOutlined style={{ color: token.colorSuccess }} />
    ),
    [EWarehouseCategorySuggestionItemStatus.NO_SUGGESTION]: (
      <MinusCircleOutlined style={{ color: token.colorWarning }} />
    ),
    [EWarehouseCategorySuggestionItemStatus.FAILED]: (
      <CloseCircleOutlined style={{ color: token.colorError }} />
    ),
  };

  const finishedCount: number = items.filter(
    (item: IWarehouseCategorySuggestionItem): boolean =>
      FINISHED_ITEM_STATUSES.includes(item.status),
  ).length;
  const percent: number = items.length
    ? Math.round((finishedCount / items.length) * 100)
    : 0;

  const countByStatus = Object.values(EWarehouseCategorySuggestionItemStatus)
    .map((itemStatus: EWarehouseCategorySuggestionItemStatus) => ({
      itemStatus,
      count: items.filter(
        (item: IWarehouseCategorySuggestionItem): boolean =>
          item.status === itemStatus,
      ).length,
    }))
    .filter(({ count }): boolean => count > 0);

  const remainingMs: number | null =
    status === EWarehouseCategorySuggestionsRunStatus.RUNNING
      ? getRemainingMs({
          startedAt,
          now: Date.now(),
          currentBatch: batch.current,
          totalBatches: batch.total,
        })
      : null;

  const summary: string[] = [WAREHOUSE_CATEGORY_SUGGESTION_STAGE_LABELS[stage]];

  if (status === EWarehouseCategorySuggestionsRunStatus.PREPARING) {
    summary.push(
      'Loading unpaired warehouse categories with unpaired products in stock…',
    );
  } else {
    summary.push(`Batch ${batch.current}/${batch.total}`);
    summary.push(
      `${finishedCount}/${items.length} warehouse categories processed`,
    );
    summary.push(`${suggestions?.length ?? 0} suggestions`);

    if (cachedCount > 0) {
      summary.push(`${cachedCount} from cache`);
    }
  }

  if (failedBatchesCount > 0) {
    summary.push(`${failedBatchesCount} failed batches`);
  }

  if (remainingMs !== null) {
    summary.push(`~${formatMinutes(remainingMs)} remaining`);
  }

  if (startedAt !== undefined && finishedAt !== undefined) {
    summary.push(`finished in ${formatMinutes(finishedAt - startedAt)}`);
  }

  if (status === EWarehouseCategorySuggestionsRunStatus.CANCELLED) {
    summary.push('cancelled');
  }

  return (
    <Space orientation="vertical" size={SPACING.sm} style={{ width: '100%' }}>
      <Progress
        percent={percent}
        status={getProgressStatus(status, failedBatchesCount)}
      />

      <Text type="secondary">{summary.join(' · ')}</Text>

      {items.length > 0 ? (
        <Collapse
          size="small"
          items={[
            {
              key: 'checklist',
              label: (
                <Space size={SPACING.sm} wrap>
                  <Text>Checklist</Text>
                  {countByStatus.map(({ itemStatus, count }) => (
                    <Tag key={itemStatus} icon={itemIcons[itemStatus]}>
                      {ITEM_STATUS_LABELS[itemStatus]}: {count}
                    </Tag>
                  ))}
                </Space>
              ),
              children: (
                <Space
                  orientation="vertical"
                  size={SPACING.xs}
                  style={{
                    width: '100%',
                    maxHeight: CHECKLIST_MAX_HEIGHT,
                    overflowY: 'auto',
                  }}
                >
                  {items.map(
                    (
                      item: IWarehouseCategorySuggestionItem,
                    ): React.ReactElement => (
                      <Space key={item.uuid} size={SPACING.sm}>
                        <span aria-label={ITEM_STATUS_LABELS[item.status]}>
                          {itemIcons[item.status]}
                        </span>
                        <Link
                          to={`/downloader/warehouse-category/${item.uuid}`}
                          label={item.name}
                          openInNewTab
                        />
                        <Text type="secondary">
                          {item.inStockProductsCount} unpaired in stock
                        </Text>
                      </Space>
                    ),
                  )}
                </Space>
              ),
            },
          ]}
        />
      ) : null}
    </Space>
  );
}

export default WarehouseCategorySuggestionsProgress;
